/**
 * Модальное окно.
 *
 * Подложка была `bg-black/60` в четырёх местах — жёсткий чёрный игнорирует
 * тему: в светлой он выглядит как выключенный монитор, а не как затемнение.
 * Берём фон темы с прозрачностью и лёгкий blur — под ним видно, что страница
 * никуда не делась, и возврат ощущается дешевле.
 *
 * Закрытие по Esc и по клику мимо окна — здесь, а не в каждом вызывающем:
 * раньше диалог маршрута не закрывался ни тем, ни другим способом, и выйти
 * можно было только крестиком.
 */
import React from 'react';
import { cn } from './cn';
import { useEscape } from '../utils/useEscape';

export const Modal: React.FC<{
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** Прижать окно к верху — для длинных форм, чтобы шапка не прыгала
   *  при разворачивании «Advanced». */
  align?: 'center' | 'top';
}> = ({ onClose, children, className, align = 'center' }) => {
  // 29.09: через общий стек слоёв — Esc закрывает только верхний слой, а не
  // модалку вместе с открытым под ней дропдауном.
  useEscape(true, onClose);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-center bg-background/80 p-4 backdrop-blur-sm animate-sb-fade-in',
        align === 'top' ? 'items-start pt-[6vh]' : 'items-center',
      )}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'flex max-h-[92vh] w-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl',
          'animate-sb-fade-zoom-in',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
};

/** Шапка диалога: имя, подпись, действия справа. */
export const ModalHeader: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, description, actions }) => (
  <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-3">
    <div className="min-w-0">
      <h2 className="truncate text-base font-semibold">{title}</h2>
      {description && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{description}</p>}
    </div>
    {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
  </div>
);

export const ModalBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto p-5', className)} {...props} />
);

/** Подвал с кнопками. Главное действие всегда крайнее справа — рука ищет его
 *  там во всех диалогах приложения, и переставлять местами нельзя. */
export const ModalFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3', className)} {...props} />
);
